import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { localizedPath, pathWithoutLocale, useCurrentLocale } from '../../lib/locale';

/**
 * Language switcher (§25.2 — shown as a Georgian flag in the design).
 *
 * Switches locale by NAVIGATING, not by calling i18n.changeLanguage directly. The URL
 * owns the locale (see LocaleLayout), so changing the language means changing the route;
 * the layout then syncs i18next. This keeps the address bar shareable and correct — the
 * "locale switcher persists / deep links work in both locales" requirement in §7.
 *
 * Staying on the equivalent page across the switch (rather than bouncing to the home
 * page) is deliberate.
 */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const locale = useCurrentLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const target = locale === 'en' ? 'ka' : 'en';

  const switchLanguage = () => {
    const bare = pathWithoutLocale(location.pathname);
    navigate(localizedPath(bare, target) + location.search, { replace: false });
  };

  return (
    <button
      type="button"
      onClick={switchLanguage}
      aria-label={t('a11y.switchLanguage')}
      lang={target}
      className={`inline-flex h-9 items-center justify-center rounded-pill border border-line px-3 text-sm font-medium text-content transition-colors hover:bg-content/5 ${className}`}
    >
      {target === 'ka' ? 'ქარ' : 'ENG'}
    </button>
  );
}
